import { compose, lifecycle } from 'recompose';
import { injectIntl } from 'react-intl';
import {
  addIntlProps,
  addProps,
  renderNothingIfPropFalsy,
  withDropdown,
} from 'utils/recompose';
import combineSelectors from 'utils/combineSelectors';
import { connect } from 'react-redux';
import messages from './messages';
import styles from './styles.module.scss';
import { fetchModelCompetition } from 'reducers/modelCompetition/actions';
import {
  competitionCompModelCodes,
  compModelCode,
} from 'reducers/modelCompetition/selectors';
import { updateSelectedChart } from 'reducers/modelPipeline/actions';
import { selectedChart } from 'reducers/modelPipeline/selectors';
import ComparisonChart from './ComparisonChart';
import CompetitionChart from './CompetitionChart';
import PipelineChart from './PipelineChart';
import AdvancedChart from './AdvancedChart';
import { modelHasTemplates, staticDataLoaded } from 'reducers/models/selectors';
import { seriesDetailPageLoaded } from 'reducers/loading/selectors';

export const chartMap = {
  advanced: AdvancedChart,
  comparison: ComparisonChart,
  competition: CompetitionChart,
  pipeline: PipelineChart,
};

const mapStateToProps = combineSelectors({
  compModelCode,
  competitionCompModelCodes,
  modelHasTemplates,
  selectedChart,
  seriesDetailPageLoaded,
  staticDataLoaded,
});

const mapDispatchToProps = {
  fetchModelCompetition,
  updateSelectedChart,
};

export const activeChart = ({
  selectedChart,
  modelHasTemplates,
  staticDataLoaded,
}) => {
  if (!staticDataLoaded) return 'comparison';

  if (!selectedChart) {
    if (modelHasTemplates) return 'comparison';
    else return 'pipeline';
  }

  if (selectedChart === 'comparison' && !modelHasTemplates) return 'pipeline';

  return selectedChart;
};

export function componentDidUpdate(prevProps) {
  const { updateSelectedChart, modelId } = this.props;
  if (prevProps.modelId !== modelId) updateSelectedChart('');
}

export const chartOptions = ({
  advancedDescription,
  advancedLabel,
  comparisonDescription,
  comparisonLabel,
  competitionDescription,
  competitionLabel,
  hasTemplates,
  pipelineDescription,
  pipelineLabel,
}) => {
  const comparisonChart = {
    chart: 'comparison',
    label: comparisonLabel,
    description: comparisonDescription,
  };

  const pipelineChart = {
    chart: 'pipeline',
    label: pipelineLabel,
    description: pipelineDescription,
  };

  // temporarily remove competition chart per client request
  const competitionChart = {
    chart: 'competition',
    label: competitionLabel,
    description: competitionDescription,
  };

  const advancedChart = {
    chart: 'advanced',
    label: advancedLabel,
    description: advancedDescription,
  };

  const allCharts = [
    comparisonChart,
    pipelineChart,
    competitionChart,
    advancedChart,
  ];

  const noTemplatesCharts = [pipelineChart, competitionChart, advancedChart];

  return hasTemplates ? allCharts : noTemplatesCharts;
};

export const activeChartDescription = ({ chartOptions, activeChart }) => {
  const activeChartOption = chartOptions.find(
    chartOption => chartOption.chart === activeChart,
  );
  return activeChartOption && activeChartOption.description;
};

export const displayCompetitionChart = ({ activeChart }) =>
  activeChart === 'competition';

export const ActiveChartComponent = ({ activeChart }) => chartMap[activeChart];

export const pipelineStyle = ({ activeChart }) =>
  activeChart === 'advanced' ? styles.pipelineLarge : styles.pipeline;

export const selectCompCode = ({ fetchModelCompetition, modelId }) => code =>
  fetchModelCompetition({ modelId, compModel: code });

export const chosenCompCode = ({
  compModelCode,
  competitionCompModelCodes,
}) => {
  let code =
    compModelCode !== '' &&
    competitionCompModelCodes &&
    competitionCompModelCodes.filter(
      compCode => compCode.code === compModelCode,
    );
  return code ? code[0].desc : '';
};

export const loaded = ({
  modelHasTemplates,
  modelTemplatesLoaded,
  seriesAllocationLoaded,
}) => modelHasTemplates || (modelTemplatesLoaded && seriesAllocationLoaded);

export const showCompetitionModelCodeDropdown = ({
  displayCompetitionChart,
  isDropdownOpen,
}) => displayCompetitionChart && isDropdownOpen;

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: [
      'comparisonLabel',
      'pipelineLabel',
      'competitionLabel',
      'advancedLabel',
      'comparisonDescription',
      'pipelineDescription',
      'advancedDescription',
      'competitionDescription',
    ],
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({ componentDidUpdate }),
  addProps({ activeChart, chartOptions }),
  withDropdown,
  addProps({
    ActiveChartComponent,
    activeChartDescription,
    chosenCompCode,
    displayCompetitionChart,
    loaded,
    pipelineStyle,
    selectCompCode,
  }),
  addProps({ showCompetitionModelCodeDropdown }),
  renderNothingIfPropFalsy('seriesDetailPageLoaded'),
);
